import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import ReactSelect from 'react-select';
import vendorService from '../../services/VendorService';

const AUD = { value: 'AUD', name: 'Australian Dollar' };
const selectedOption = (options, reference) =>
  options.find((option) => String(option.value) === String(reference?.value || '')) || null;
const optionToRef = (option) => (option ? { value: String(option.value), name: option.label } : null);

const CreateCustomerDialog = ({ open, initialName = '', onCancel, onSave }) => {
  const [terms, setTerms] = useState([]);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    Title: '',
    GivenName: '',
    MiddleName: '',
    FamilyName: '',
    Suffix: '',
    DisplayName: initialName,
    CompanyName: '',
    Email: '',
    Phone: '',
    Mobile: '',
    Fax: '',
    AlternatePhone: '',
    Website: '',
    CurrencyRef: AUD,
    BillLine1: '',
    BillLine2: '',
    BillCity: '',
    BillState: '',
    BillPostalCode: '',
    BillCountry: 'Australia',
    ShippingSameAsBilling: true,
    ShipLine1: '',
    ShipLine2: '',
    ShipCity: '',
    ShipState: '',
    ShipPostalCode: '',
    ShipCountry: 'Australia',
    Notes: '',
    SalesTermRef: null,
    PreferredDeliveryMethod: 'Print',
    PrimaryTaxIdentifier: '',
    Taxable: false
  });

  useEffect(() => {
    if (open) setForm((current) => ({ ...current, DisplayName: initialName }));
  }, [initialName, open]);

  useEffect(() => {
    let active = true;
    const fetchTerms = async () => {
      setTermsLoading(true);
      setTermsError('');
      try {
        const response = await vendorService.getTermsFromQBO();
        const qboTerms = response?.data?.data?.QueryResponse?.Term || [];
        if (active) {
          setTerms(
            qboTerms
              .filter((term) => term.Active !== false && term.Id && term.Name)
              .map((term) => ({ value: String(term.Id), label: term.Name }))
          );
        }
      } catch (error) {
        if (active) setTermsError(error?.response?.data?.message || error?.message || 'Could not load QBO terms');
      } finally {
        if (active) setTermsLoading(false);
      }
    };
    if (open) fetchTerms();
    return () => {
      active = false;
    };
  }, [open]);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const address = (prefix) => ({
    Line1: form[`${prefix}Line1`].trim(),
    Line2: form[`${prefix}Line2`].trim(),
    City: form[`${prefix}City`].trim(),
    CountrySubDivisionCode: form[`${prefix}State`].trim(),
    PostalCode: form[`${prefix}PostalCode`].trim(),
    Country: form[`${prefix}Country`].trim()
  });

  const save = async () => {
    const DisplayName = form.DisplayName.trim();
    if (!DisplayName || saving) return;
    const billAddr = address('Bill');
    setSaving(true);
    try {
      await onSave({
        Title: form.Title.trim(),
        GivenName: form.GivenName.trim(),
        MiddleName: form.MiddleName.trim(),
        FamilyName: form.FamilyName.trim(),
        Suffix: form.Suffix.trim(),
        DisplayName,
        CompanyName: form.CompanyName.trim(),
        PrimaryEmailAddr: { Address: form.Email.trim() },
        PrimaryPhone: { FreeFormNumber: form.Phone.trim() },
        Mobile: { FreeFormNumber: form.Mobile.trim() },
        Fax: { FreeFormNumber: form.Fax.trim() },
        AlternatePhone: { FreeFormNumber: form.AlternatePhone.trim() },
        WebAddr: { URI: form.Website.trim() },
        CurrencyRef: form.CurrencyRef,
        BillAddr: billAddr,
        ShipAddr: form.ShippingSameAsBilling ? billAddr : address('Ship'),
        Notes: form.Notes.trim(),
        SalesTermRef: form.SalesTermRef,
        PreferredDeliveryMethod: form.PreferredDeliveryMethod,
        PrimaryTaxIdentifier: form.PrimaryTaxIdentifier.trim(),
        Taxable: form.Taxable
      });
    } finally {
      setSaving(false);
    }
  };

  const selectProps = {
    isClearable: true,
    isSearchable: true,
    // eslint-disable-next-line no-undef
    menuPortalTarget: document.body,
    menuPosition: 'fixed',
    styles: { menuPortal: (base) => ({ ...base, zIndex: 1400 }) }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} fullWidth maxWidth="md">
      <DialogTitle>Create customer</DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#f3f4f6' }}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Name and contact
          </Typography>
          <TextField
            label="Currency"
            value={`${form.CurrencyRef.value} ${form.CurrencyRef.name}`}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
            fullWidth
          />
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
            <TextField
              label="Title"
              value={form.Title}
              onChange={(event) => updateField('Title', event.target.value)}
            />
            <TextField
              label="First name"
              value={form.GivenName}
              onChange={(event) => updateField('GivenName', event.target.value)}
            />
            <TextField
              label="Middle name"
              value={form.MiddleName}
              onChange={(event) => updateField('MiddleName', event.target.value)}
            />
            <TextField
              label="Last name"
              value={form.FamilyName}
              onChange={(event) => updateField('FamilyName', event.target.value)}
            />
            <TextField
              label="Suffix"
              value={form.Suffix}
              onChange={(event) => updateField('Suffix', event.target.value)}
            />
            <TextField
              label="Customer display name"
              value={form.DisplayName}
              onChange={(event) => updateField('DisplayName', event.target.value)}
              required
            />
            <TextField
              label="Company name"
              value={form.CompanyName}
              onChange={(event) => updateField('CompanyName', event.target.value)}
            />
            <TextField
              label="Email"
              value={form.Email}
              onChange={(event) => updateField('Email', event.target.value)}
            />
            <TextField
              label="Phone number"
              value={form.Phone}
              onChange={(event) => updateField('Phone', event.target.value)}
            />
            <TextField
              label="Mobile number"
              value={form.Mobile}
              onChange={(event) => updateField('Mobile', event.target.value)}
            />
            <TextField label="Fax" value={form.Fax} onChange={(event) => updateField('Fax', event.target.value)} />
            <TextField
              label="Other phone"
              value={form.AlternatePhone}
              onChange={(event) => updateField('AlternatePhone', event.target.value)}
            />
            <TextField
              label="Website"
              value={form.Website}
              onChange={(event) => updateField('Website', event.target.value)}
            />
          </div>
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Addresses
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Billing address
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <TextField
              label="Street address 1"
              value={form.BillLine1}
              onChange={(event) => updateField('BillLine1', event.target.value)}
            />
            <TextField
              label="Street address 2"
              value={form.BillLine2}
              onChange={(event) => updateField('BillLine2', event.target.value)}
            />
            <TextField
              label="City"
              value={form.BillCity}
              onChange={(event) => updateField('BillCity', event.target.value)}
            />
            <TextField
              label="State"
              value={form.BillState}
              onChange={(event) => updateField('BillState', event.target.value)}
            />
            <TextField
              label="Postcode"
              value={form.BillPostalCode}
              onChange={(event) => updateField('BillPostalCode', event.target.value)}
            />
            <TextField
              label="Country"
              value={form.BillCountry}
              onChange={(event) => updateField('BillCountry', event.target.value)}
            />
          </div>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={form.ShippingSameAsBilling}
                onChange={(event) => updateField('ShippingSameAsBilling', event.target.checked)}
              />
            }
            label="Shipping address is the same as billing"
          />
          {!form.ShippingSameAsBilling && (
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mt-2">
              <TextField
                label="Shipping street address 1"
                value={form.ShipLine1}
                onChange={(event) => updateField('ShipLine1', event.target.value)}
              />
              <TextField
                label="Shipping street address 2"
                value={form.ShipLine2}
                onChange={(event) => updateField('ShipLine2', event.target.value)}
              />
              <TextField
                label="Shipping city"
                value={form.ShipCity}
                onChange={(event) => updateField('ShipCity', event.target.value)}
              />
              <TextField
                label="Shipping state"
                value={form.ShipState}
                onChange={(event) => updateField('ShipState', event.target.value)}
              />
              <TextField
                label="Shipping postcode"
                value={form.ShipPostalCode}
                onChange={(event) => updateField('ShipPostalCode', event.target.value)}
              />
              <TextField
                label="Shipping country"
                value={form.ShipCountry}
                onChange={(event) => updateField('ShipCountry', event.target.value)}
              />
            </div>
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Notes
          </Typography>
          <TextField
            label="Notes"
            value={form.Notes}
            onChange={(event) => updateField('Notes', event.target.value)}
            minRows={3}
            multiline
            fullWidth
          />
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Payments
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <div>
              <Typography variant="caption" color="text.secondary">
                Terms
              </Typography>
              <ReactSelect
                {...selectProps}
                inputId="create-customer-terms"
                aria-label="Terms"
                isLoading={termsLoading}
                options={terms}
                value={selectedOption(terms, form.SalesTermRef)}
                onChange={(option) => updateField('SalesTermRef', optionToRef(option))}
                placeholder="Select QBO terms"
              />
              {termsError && (
                <Typography variant="caption" color="error">
                  {termsError}
                </Typography>
              )}
            </div>
            <TextField
              select
              label="Sales form delivery option"
              value={form.PreferredDeliveryMethod}
              onChange={(event) => updateField('PreferredDeliveryMethod', event.target.value)}
            >
              <MenuItem value="Print">Print</MenuItem>
              <MenuItem value="Email">Email</MenuItem>
              <MenuItem value="None">None</MenuItem>
            </TextField>
          </div>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Additional info
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <TextField
              label="Australian Business Number"
              value={form.PrimaryTaxIdentifier}
              onChange={(event) => updateField('PrimaryTaxIdentifier', event.target.value)}
            />
            <FormControlLabel
              control={
                <Checkbox checked={form.Taxable} onChange={(event) => updateField('Taxable', event.target.checked)} />
              }
              label="Taxable customer"
            />
          </div>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={save} variant="contained" disabled={saving || !form.DisplayName.trim()}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateCustomerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initialName: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default CreateCustomerDialog;
